import React, {
  useState, useEffect, useContext
} from 'react';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { string, number, bool } from 'prop-types';
import {
  bool as boolType,
  client,
  params,
  arrayOf as arrayOfType,
  number as numberType,
  shape,
  string as stringType,
  useDataModel,
  extend,
  QueryProvider
} from '@thd-nucleus/data-sources';
import {
  Button
} from '@one-thd/sui-atomic-components';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { ProductPodUtils } from '@thd-olt-component-react/product-pod';
import { AddToQuoteDrawer } from './drawers/AddToQuoteDrawer';

const customerInfoCache = {
  svocId: null,
  userId: null
};

const AddToQuote = ({
  itemId,
  quantity,
  fulfillmentLocation,
  fulfillmentMethod,
  disabled
}) => {
  const { deliveryZip } = useContext(ExperienceContext);
  const store = useStore();
  const [drawerShowing, setDrawerShowing] = useState(false);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('add-to-quote.ready');
  }, []);

  /** Use new hook once ready */
  if (!customerInfoCache.svocID) {
    if (typeof window !== 'undefined') {
      const { svocID, userID, role } = window.THDCustomer?.default;
      customerInfoCache.svocId = svocID;
      customerInfoCache.userId = userID;
      customerInfoCache.role = role;
    }
  }

  const { data, error: errorProduct } = useDataModel('product', {
    variables: {
      itemId,
    },
    skip: !itemId,
  });

  const product = data?.product;
  const { pricing } = product || {};

  let isDisabled;
  if (disabled === null) {
    isDisabled = !podFulFillmentUtils.isATCEnabled(product) || podFulFillmentUtils.isAppliance(product);
  } else {
    isDisabled = disabled;
  }
  const fulfillmentMethodFromFulfillmentComponent = podFulFillmentUtils.getFulfillmentMethod(product);
  const fulfillmentLocationFromProductPodUtils = ProductPodUtils.getFulfillmentLocation({
    fulfillmentMethod: fulfillmentMethodFromFulfillmentComponent, store, product, deliveryZip
  });

  const openDrawer = () => {
    setDrawerShowing(true);
  };

  return (
    <>
      <Button
        variant="secondary"
        fullWidth
        onClick={openDrawer}
        disabled={isDisabled}
        data-testid="add-to-quote"
      >
        Add to Quote
      </Button>
      <QueryProvider
        cacheKey="add-to-quote-drawer"
        defaultVariables={{ svocId: customerInfoCache.svocId }}
      >
        <AddToQuoteDrawer
          itemId={itemId}
          quantity={quantity}
          customerInfo={customerInfoCache}
          localStoreId={store.storeId}
          fulfillmentMethod={fulfillmentMethod || fulfillmentMethodFromFulfillmentComponent}
          fulfillmentLocation={fulfillmentLocation || fulfillmentLocationFromProductPodUtils}
          price={pricing?.value}
          drawerShowing={drawerShowing}
          setDrawerShowing={setDrawerShowing}
        />
      </QueryProvider>
    </>
  );
};

AddToQuote.propTypes = {
  itemId: string.isRequired,
  quantity: number.isRequired,
  fulfillmentMethod: string,
  fulfillmentLocation: string,
  disabled: bool,
};

AddToQuote.defaultProps = {
  fulfillmentMethod: '',
  fulfillmentLocation: '',
  disabled: null,
};

AddToQuote.displayName = 'AddToQuote';

AddToQuote.dataModel = extend(
  {
    product: params({
      itemId: stringType().isRequired()
    }).shape({
      pricing: params({ storeId: stringType() }).shape({
        value: numberType({ float: true }),
      }),
      fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
        anchorStoreStatus: boolType(),
        anchorStoreStatusType: stringType(),
        backordered: boolType(),
        backorderedShipDate: stringType(),
        bossExcludedShipStates: stringType(),
        excludedShipStates: stringType(),
        seasonStatusEligible: boolType(),
        fulfillmentOptions: arrayOfType(shape({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOfType(shape({
            deliveryTimeline: stringType(),
            deliveryDates: shape({
              startDate: stringType(),
              endDate: stringType(),
            }),
            deliveryCharge: stringType(),
            dynamicEta: shape({
              hours: stringType(),
              minutes: stringType()
            }),
            hasFreeShipping: boolType(),
            freeDeliveryThreshold: numberType({ float: true }),
            locations: arrayOfType(shape({
              curbsidePickupFlag: boolType(),
              isBuyInStoreCheckNearBy: boolType(),
              distance: numberType({ float: true }),
              inventory: shape({
                isOutOfStock: boolType(),
                isInStock: boolType(),
                isLimitedQuantity: boolType(),
                isUnavailable: boolType(),
                quantity: numberType(),
                maxAllowedBopisQty: numberType(),
                minAllowedBopisQty: numberType()
              }),
              isAnchor: boolType(),
              locationId: stringType(),
              state: stringType(),
              storeName: stringType(),
              storePhone: stringType(),
              type: stringType(),
            })),
            type: stringType(),
            totalCharge: numberType({ float: true }),
          }))
        })),
        onlineStoreStatus: boolType(),
        onlineStoreStatusType: stringType(),
      }))
    })
  },
  AddToQuoteDrawer,
);

export { AddToQuote };